<template>
  <div class="game-hub-page">
    <div v-if="gameHub" class="game-hub-page__shell">
      <section class="game-hub-page__hero">
        <div class="game-hub-page__copy">
          <span class="game-hub-page__eyebrow">{{ gameHub.sourceLabel || 'Game Hub' }}</span>
          <h1>{{ gameHub.name }}</h1>
          <p class="game-hub-page__description">{{ gameHub.description }}</p>

          <div class="game-hub-page__meta">
            <span>#{{ gameHub.rank }}</span>
            <span>{{ formatDate(gameHub.lastSeen || gameHub.updateDate) }}</span>
            <span>{{ (gameHub.platforms || []).join(', ') }}</span>
            <span v-if="gameHub.articleCount">{{ gameHub.articleCount }} related articles</span>
          </div>

          <div v-if="gameHub.tags && gameHub.tags.length" class="game-hub-page__tags">
            <span
              v-for="tag in gameHub.tags"
              :key="`${gameHub.slug}-${tag}`"
              class="game-hub-page__tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>

        <div v-if="gameHub.image" class="game-hub-page__media">
          <img :src="gameHub.image" :alt="gameHub.name" loading="lazy" />
        </div>
      </section>

      <section class="game-hub-page__grid">
        <article class="game-hub-page__card">
          <h2>Snapshot</h2>
          <dl>
            <div>
              <dt>Source</dt>
              <dd>{{ gameHub.sourceLabel || 'Steam' }}</dd>
            </div>
            <div>
              <dt>Steam App ID</dt>
              <dd>{{ gameHub.steamAppId || 'N/A' }}</dd>
            </div>
            <div>
              <dt>Price</dt>
              <dd>{{ gameHub.price || 'Unavailable' }}</dd>
            </div>
            <div>
              <dt>Discount</dt>
              <dd>{{ gameHub.discount || 'None listed' }}</dd>
            </div>
          </dl>
        </article>

        <article class="game-hub-page__card">
          <h2>Coverage</h2>
          <p v-if="gameHub.latestArticleTitle" class="game-hub-page__coverage-copy">
            Latest related coverage: <a :href="gameHub.latestArticleUrl">{{ gameHub.latestArticleTitle }}</a>
          </p>
          <p v-else class="game-hub-page__coverage-copy">
            No GamesNest coverage yet. This is a fresh candidate for independent-site evaluation.
          </p>
        </article>
      </section>

      <section v-if="relatedArticles.length" class="game-hub-page__related">
        <div class="game-hub-page__section-head">
          <h2>Related Coverage</h2>
          <a href="/games/">Browse More Games</a>
        </div>

        <div class="game-hub-page__article-grid">
          <a
            v-for="article in relatedArticles"
            :key="article.url"
            :href="article.url"
            class="game-hub-page__article-card"
          >
            <div v-if="article.featuredImage" class="game-hub-page__article-image">
              <img :src="article.featuredImage" :alt="article.title" loading="lazy" />
            </div>
            <div class="game-hub-page__article-body">
              <span class="game-hub-page__article-category">{{ article.category }}</span>
              <h3>{{ article.title }}</h3>
              <p>{{ article.excerpt }}</p>
            </div>
          </a>
        </div>
      </section>
    </div>

    <div v-else class="game-hub-page__not-found">
      <h1>Game Not Found</h1>
      <p>The game hub you're looking for doesn't exist or has been removed.</p>
      <router-link to="/games/">Browse Games</router-link>
    </div>
  </div>
</template>

<script>
import { generateMetaTags } from '@/utils/seo'

export default {
  name: 'GameHubPage',
  async asyncData({ store, route, context }) {
    const slug = route.params.slug
    const gameHub = await store.dispatch('gameHubs/fetchGameHubBySlug', slug)
    if (!gameHub && context) {
      context.code = 404
    }
  },
  computed: {
    gameHub() {
      return this.$store.state.gameHubs.currentGameHub
    },
    relatedArticles() {
      return (this.gameHub && this.gameHub.relatedArticles) || []
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
    generateMetaTags,
  },
  metaInfo() {
    if (!this.gameHub) return {}

    return generateMetaTags({
      title: this.gameHub.metaTitle || this.gameHub.name,
      description: this.gameHub.metaDescription || this.gameHub.description,
      path: this.$route.path,
      type: 'website',
      image: this.gameHub.image,
    })
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.game-hub-page {
  max-width: rem(1120);
  margin: 0 auto;
  padding: rem(24);

  @include pc {
    padding: rem(40) rem(24) rem(72);
  }

  &__shell {
    display: flex;
    flex-direction: column;
    gap: rem(28);
  }

  &__hero {
    display: grid;
    grid-template-columns: 1fr;
    gap: rem(20);
    padding: rem(20);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: rem(22);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.015));

    @include pc {
      grid-template-columns: minmax(0, 1.15fr) rem(360);
      align-items: center;
      padding: rem(28);
    }
  }

  &__copy h1 {
    margin: rem(10) 0 rem(12);
    font-size: rem(34);
    line-height: 1.08;

    @include pc {
      font-size: rem(48);
    }
  }

  &__eyebrow,
  &__tag,
  &__article-category {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: rem(4) rem(10);
    border-radius: rem(999);
    background: rgba($primary-color, 0.14);
    color: $primary-color;
    font-size: rem(12);
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  &__description {
    color: $text-secondary;
    font-size: rem(16);
    line-height: 1.75;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: rem(10);
    margin-top: rem(18);

    span {
      display: inline-flex;
      align-items: center;
      padding: rem(6) rem(10);
      border-radius: rem(999);
      background: rgba(255, 255, 255, 0.06);
      color: $text-secondary;
      font-size: rem(13);
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: rem(10);
    margin-top: rem(18);
  }

  &__tag {
    background: rgba(255, 255, 255, 0.06);
    color: $text-primary;
    letter-spacing: 0;
    text-transform: none;
  }

  &__media img {
    width: 100%;
    border-radius: rem(18);
    display: block;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: rem(18);

    @include pc {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  &__card {
    padding: rem(20);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: rem(18);
    background: rgba(255, 255, 255, 0.03);

    h2 {
      margin: 0 0 rem(16);
      font-size: rem(22);
    }

    dl {
      display: grid;
      gap: rem(14);
      margin: 0;
    }

    dt {
      margin-bottom: rem(4);
      color: $text-secondary;
      font-size: rem(13);
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    dd {
      margin: 0;
      color: $text-primary;
      font-size: rem(16);
    }
  }

  &__coverage-copy {
    margin: 0;
    color: $text-secondary;
    line-height: 1.7;

    a {
      color: $text-primary;
      text-decoration: none;
    }
  }

  &__section-head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: rem(12);
    margin-bottom: rem(16);

    h2 {
      margin: 0;
      font-size: rem(24);
    }

    a {
      color: $primary-color;
      text-decoration: none;
      font-weight: 700;
    }
  }

  &__article-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: rem(16);

    @include pc {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  &__article-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: rem(18);
    background: rgba(255, 255, 255, 0.03);
    color: inherit;
    text-decoration: none;
  }

  &__article-image img {
    width: 100%;
    height: rem(170);
    object-fit: cover;
    display: block;
  }

  &__article-body {
    display: flex;
    flex-direction: column;
    gap: rem(10);
    padding: rem(16);

    h3 {
      margin: 0;
      font-size: rem(18);
      line-height: 1.3;
    }

    p {
      margin: 0;
      color: $text-secondary;
      line-height: 1.6;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__not-found {
    padding: rem(72) rem(20);
    text-align: center;

    p {
      color: $text-secondary;
    }
  }
}
</style>
